import { formatDate } from "@/lib/format";
import type { ArtikelRow } from "@/lib/artikel";

// match_reasons und deleted_at sind Badges, keine Spalten.
export type ArtikelColumnKey = Exclude<keyof ArtikelRow, "match_reasons" | "deleted_at">;

export const ARTIKEL_REQUIRED_COLUMNS = ["hero_nr"] as const;

export const ARTIKEL_COLUMNS: ArtikelColumnKey[] = [
  "id",
  "hero_nr",
  "hero_base_name",
  "hero_base_description",
  "hero_base_unit_type",
  "hero_base_ean",
  "hero_base_category",
  "hero_base_matchcode",
  "hero_base_manufacturer",
  "hero_base_manufacturer_nr",
  "hero_base_supplier_nr",
  "hero_base_manufacturer_type_name",
  "hero_base_delivery_time",
  "hero_base_external_url",
  "hero_base_image_src",
  "hero_base_is_deleted",
  "hero_is_deleted",
  "hero_supply_operator_name",
  "lieferant_geschaeftspartner_id",
  "lieferant_geschaeftspartner_name",
  "import_geschuetzt",
  "hero_default_sales_net_price",
  "hero_list_price",
  "hero_base_price",
  "hero_vat_percent",
  "preis_bruttopreis",
  "preis_nettopreis",
  "preis_verkaufspreis",
  "preis_rabatt",
  "preis_aufschlag",
  "preis_preismenge",
  "preis_einheit",
  "preis_gueltig_bis",
];

/** Hero-Preisspalten (aus dem Produkt-Sync). */
export const ARTIKEL_HERO_PRICE_COLUMNS: ArtikelColumnKey[] = [
  "hero_default_sales_net_price",
  "hero_list_price",
  "hero_base_price",
  "hero_vat_percent",
];

/** Alle Preisspalten (Hero + Import) – für Monteure ausblenden. */
export function isArtikelPriceColumn(key: string): boolean {
  return (
    key.startsWith("preis_") ||
    ARTIKEL_HERO_PRICE_COLUMNS.includes(key as ArtikelColumnKey)
  );
}

/** Technische Felder standardmäßig ausblenden – über „Spalten“ aktivierbar. */
export const ARTIKEL_HIDDEN_BY_DEFAULT: ArtikelColumnKey[] = [
  "id",
  "hero_base_external_url",
  "hero_base_image_src",
  "hero_base_is_deleted",
  "hero_is_deleted",
  "lieferant_geschaeftspartner_id",
  "lieferant_geschaeftspartner_name",
  "preis_bruttopreis",
  "preis_rabatt",
  "preis_aufschlag",
  "preis_preismenge",
  "preis_gueltig_bis",
  "hero_base_manufacturer_nr",
  "hero_base_manufacturer_type_name",
  "hero_base_delivery_time",
  "hero_base_matchcode",
  "import_geschuetzt",
  "hero_base_price",
  "hero_vat_percent",
];

const COLUMN_LABELS: Record<ArtikelColumnKey, string> = {
  id: "ID",
  hero_nr: "Artikelnummer",
  hero_base_name: "Name",
  hero_base_description: "Beschreibung",
  hero_base_unit_type: "Einheit",
  hero_base_ean: "EAN",
  hero_base_category: "Kategorie",
  hero_base_matchcode: "Matchcode",
  hero_base_manufacturer: "Hersteller",
  hero_base_manufacturer_nr: "Hersteller-Nr.",
  hero_base_supplier_nr: "Lieferanten-Nr.",
  hero_base_manufacturer_type_name: "Hersteller-Typ",
  hero_base_delivery_time: "Lieferzeit (Basis)",
  hero_base_external_url: "Externe URL",
  hero_base_image_src: "Bild",
  hero_base_is_deleted: "Basis gelöscht",
  hero_is_deleted: "Hero gelöscht",
  hero_supply_operator_name: "Lieferant",
  lieferant_geschaeftspartner_id: "Lieferant Kontakt-ID",
  lieferant_geschaeftspartner_name: "Lieferant (Kontakte)",
  import_geschuetzt: "Importschutz",
  hero_default_sales_net_price: "VK netto (Hero)",
  hero_list_price: "Listenpreis (Hero)",
  hero_base_price: "Basispreis (Hero)",
  hero_vat_percent: "MwSt % (Hero)",
  preis_bruttopreis: "Bruttopreis",
  preis_nettopreis: "Nettopreis",
  preis_verkaufspreis: "Verkaufspreis",
  preis_rabatt: "Rabatt %",
  preis_aufschlag: "Aufschlag %",
  preis_preismenge: "Preismenge",
  preis_einheit: "Preiseinheit",
  preis_gueltig_bis: "Preis gültig bis",
};

const DEFAULT_WIDTHS: Partial<Record<ArtikelColumnKey, number>> = {
  id: 72,
  hero_nr: 110,
  hero_base_name: 200,
  hero_base_description: 280,
  hero_base_unit_type: 88,
  hero_base_ean: 140,
  hero_base_image_src: 72,
  hero_base_category: 140,
  hero_base_matchcode: 120,
  hero_base_manufacturer: 160,
  hero_base_supplier_nr: 140,
  hero_supply_operator_name: 180,
  hero_default_sales_net_price: 130,
  hero_list_price: 130,
  hero_base_price: 130,
  hero_vat_percent: 100,
  preis_nettopreis: 110,
  preis_verkaufspreis: 120,
  preis_einheit: 100,
};

export const ARTIKEL_COL_MIN_WIDTH = 72;
export const ARTIKEL_COL_MAX_WIDTH = 480;

/** Spalten mit Filterfeld in der Tabellen-Kopfzeile → API-Query-Key `f_*`. */
export const ARTIKEL_FILTERABLE_COLUMNS: Partial<
  Record<ArtikelColumnKey, { param: string; kind: "text" | "bool" }>
> = {
  hero_nr: { param: "f_hero_nr", kind: "text" },
  hero_base_name: { param: "f_name", kind: "text" },
  hero_base_description: { param: "f_beschreibung", kind: "text" },
  hero_base_matchcode: { param: "f_matchcode", kind: "text" },
  hero_base_ean: { param: "f_ean", kind: "text" },
  hero_base_category: { param: "f_kategorie", kind: "text" },
  hero_base_manufacturer: { param: "f_hersteller", kind: "text" },
  hero_base_manufacturer_nr: { param: "f_hersteller_nr", kind: "text" },
  hero_base_supplier_nr: { param: "f_lieferanten_nr", kind: "text" },
  hero_base_unit_type: { param: "f_einheit", kind: "text" },
  hero_supply_operator_name: { param: "f_lieferant", kind: "text" },
  lieferant_geschaeftspartner_name: { param: "f_lieferant", kind: "text" },
  import_geschuetzt: { param: "f_import_geschuetzt", kind: "bool" },
};

export function isArtikelFilterableColumn(key: ArtikelColumnKey): boolean {
  return key in ARTIKEL_FILTERABLE_COLUMNS;
}

export function getAvailableArtikelColumns(): ArtikelColumnKey[] {
  return [...ARTIKEL_COLUMNS];
}

export function getDefaultArtikelColumns(): ArtikelColumnKey[] {
  return ARTIKEL_COLUMNS.filter((col) => !ARTIKEL_HIDDEN_BY_DEFAULT.includes(col));
}

export function normalizeArtikelColumnSelection(
  selected: string[] | null | undefined,
  available: ArtikelColumnKey[] = getAvailableArtikelColumns(),
): ArtikelColumnKey[] {
  const allowed = new Set(available);
  const out: ArtikelColumnKey[] = [];
  for (const col of selected || []) {
    if (allowed.has(col as ArtikelColumnKey) && !out.includes(col as ArtikelColumnKey)) {
      out.push(col as ArtikelColumnKey);
    }
  }
  for (const required of ARTIKEL_REQUIRED_COLUMNS) {
    if (!out.includes(required)) out.unshift(required);
  }
  if (out.length === 0) return getDefaultArtikelColumns();
  // Migration: gespeicherte Spaltenauswahl von vor dem Preis-Sync kennt die
  // Hero-Preisspalten noch nicht – Standard-VK/Listenpreis einmalig ergänzen.
  const hasHeroPrice = ARTIKEL_HERO_PRICE_COLUMNS.some((col) => out.includes(col));
  if (!hasHeroPrice) {
    if (allowed.has("hero_default_sales_net_price")) out.push("hero_default_sales_net_price");
    if (allowed.has("hero_list_price")) out.push("hero_list_price");
  }
  return out;
}

export function getArtikelColumnLabel(key: string): string {
  return COLUMN_LABELS[key as ArtikelColumnKey] ?? key;
}

export function getArtikelColumnDefaultWidth(key: string): number {
  return DEFAULT_WIDTHS[key as ArtikelColumnKey] ?? 140;
}

export function clampArtikelColumnWidth(width: number): number {
  return Math.min(ARTIKEL_COL_MAX_WIDTH, Math.max(ARTIKEL_COL_MIN_WIDTH, Math.round(width)));
}

function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 4 }).format(value);
}

function formatBool(value: boolean | null | undefined): string {
  if (value == null) return "—";
  return value ? "Ja" : "Nein";
}

export interface ArtikelCellValue {
  value: string;
  mono?: boolean;
  alignRight?: boolean;
  kind?: "lieferant" | "image";
  lieferantGeschaeftspartnerId?: number | null;
}

export function renderArtikelCellValue(row: ArtikelRow, key: ArtikelColumnKey): ArtikelCellValue {
  if (key === "hero_base_image_src") {
    const src = row.hero_base_image_src ? String(row.hero_base_image_src) : "";
    return { value: src || "—", kind: "image" };
  }

  if (key === "hero_supply_operator_name") {
    const name = row.hero_supply_operator_name;
    const gpId = row.lieferant_geschaeftspartner_id;
    if (name && gpId) {
      return {
        value: String(name),
        kind: "lieferant",
        lieferantGeschaeftspartnerId: gpId,
      };
    }
  }

  const raw = renderArtikelCell(row, key);
  const isHeroPrice = ARTIKEL_HERO_PRICE_COLUMNS.includes(key);
  const mono =
    key === "hero_nr" ||
    key === "hero_base_ean" ||
    key === "id" ||
    key.startsWith("preis_") ||
    isHeroPrice ||
    key.endsWith("_id") ||
    key === "preis_gueltig_bis";
  const alignRight = (key.startsWith("preis_") && key !== "preis_einheit") || isHeroPrice;
  return { value: raw, mono, alignRight };
}

export function renderArtikelCell(row: ArtikelRow, key: ArtikelColumnKey): string {
  const value = row[key];

  if (value == null || value === "") return "—";

  if (typeof value === "boolean") return formatBool(value);

  if (key === "preis_gueltig_bis") return formatDate(String(value));

  if (key.startsWith("preis_") && key !== "preis_einheit" && key !== "preis_preismenge") {
    return formatNumber(Number(value));
  }

  if (key === "hero_vat_percent") {
    return `${formatNumber(Number(value))} %`;
  }

  if (ARTIKEL_HERO_PRICE_COLUMNS.includes(key)) {
    return `${new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(Number(value))} €`;
  }

  if (typeof value === "number") return String(value);

  return String(value);
}
